/**
 * Hooks de TanStack Query para el módulo de Merchants.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  exportMerchantsCsv,
  getMerchant,
  getMerchant360,
  getMerchantAudit,
  getMerchants,
  MerchantApiError,
  updateMerchant,
} from '../../../api/merchants.api';
import type {
  Merchant,
  Merchant360,
  MerchantAuditEntry,
  MerchantEditPayload,
  MerchantListParams,
  MerchantListResponse,
  MerchantStatus,
} from '../types/merchant.types';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const merchantKeys = {
  all: ['merchants'] as const,
  lists: () => [...merchantKeys.all, 'list'] as const,
  list: (params: MerchantListParams) => [...merchantKeys.lists(), params] as const,
  details: () => [...merchantKeys.all, 'detail'] as const,
  detail: (id: string) => [...merchantKeys.details(), id] as const,
  view360: (id: string) => [...merchantKeys.all, '360', id] as const,
  audit: (id: string) => [...merchantKeys.all, 'audit', id] as const,
};

// ─── Query Hooks ────────────────────────────────────────────────────────────

/**
 * Hook para listar merchants con paginación y filtros.
 */
export function useMerchants(params: MerchantListParams) {
  return useQuery<MerchantListResponse, MerchantApiError>({
    queryKey: merchantKeys.list(params),
    queryFn: () => getMerchants(params),
    staleTime: 30_000, // 30 segundos
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener detalle de un merchant.
 */
export function useMerchant(id: string | undefined) {
  return useQuery<Merchant, MerchantApiError>({
    queryKey: merchantKeys.detail(id ?? ''),
    queryFn: () => getMerchant(id!),
    enabled: Boolean(id),
    staleTime: 60_000, // 1 minuto
  });
}

/**
 * Hook para obtener vista 360 consolidada de un merchant.
 * Incluye cuentas, clientes asociados y actividad reciente.
 */
export function useMerchant360(id: string | undefined) {
  return useQuery<Merchant360, MerchantApiError>({
    queryKey: merchantKeys.view360(id ?? ''),
    queryFn: () => getMerchant360(id!),
    enabled: Boolean(id),
    staleTime: 30_000, // 30 segundos
  });
}

/**
 * Hook para obtener historial de auditoría de un merchant.
 * Trazabilidad total (P-03).
 */
export function useMerchantAudit(id: string | undefined) {
  return useQuery<MerchantAuditEntry[], MerchantApiError>({
    queryKey: merchantKeys.audit(id ?? ''),
    queryFn: () => getMerchantAudit(id!),
    enabled: Boolean(id),
    staleTime: 60_000, // 1 minuto
  });
}

// ─── Mutation Hooks ─────────────────────────────────────────────────────────

interface UpdateMerchantVariables {
  id: string;
  payload: MerchantEditPayload;
}

/**
 * Hook para actualizar datos de un merchant.
 * Invalida queries relacionadas tras éxito.
 *
 * @rule RULE-29 — Solo PSP_ADMIN puede cambiar estado
 * @rule RULE-16 — Motivo obligatorio en cambio de estado
 */
export function useUpdateMerchant() {
  const queryClient = useQueryClient();

  return useMutation<Merchant, MerchantApiError, UpdateMerchantVariables>({
    mutationFn: ({ id, payload }) => updateMerchant(id, payload),
    onSuccess: (data, variables) => {
      // Actualizar cache del detalle
      queryClient.setQueryData(merchantKeys.detail(variables.id), data);
      // Invalidar vista 360 para refetch
      queryClient.invalidateQueries({ queryKey: merchantKeys.view360(variables.id) });
      // Invalidar listados
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
      // Invalidar auditoría
      queryClient.invalidateQueries({ queryKey: merchantKeys.audit(variables.id) });
    },
  });
}

interface ExportMerchantsCsvVariables {
  estado?: MerchantStatus;
  search?: string;
}

/**
 * Hook para exportar merchants a CSV.
 */
export function useExportMerchantsCsv() {
  return useMutation<Blob, MerchantApiError, ExportMerchantsCsvVariables>({
    mutationFn: (params) => exportMerchantsCsv(params),
    onSuccess: (blob) => {
      // Descargar archivo
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `merchants-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
